/**
 * Shared attack delivery / travel / VFX preset resolve (no Phaser).
 * Used by Dropfront, Art Lab, and FFA math helpers (`lerp2`).
 */

export type AttackDelivery = "arc" | "bolt" | "pulse";

export type SpriteFxAt = "origin" | "impact";

export type AttackFxRoleConfig = {
  delivery: AttackDelivery;
  /** Fraction of swingSec until impact (arc/pulse). */
  impactFrac: number;
  /** Bolt travel speed in cell-units per second. */
  speedCellsPerSec?: number;
  travelMinSec?: number;
  travelMaxSec?: number;
  originPreset: string;
  flightPreset: string;
  hitPreset: string;
  /** Ally-as-victim hit (hurt reaction; not attacker claw/slash). */
  takenHitPreset?: string;
  volumeRadius?: number;
  /**
   * Art pack sprite id for affiliation-rimmed FX layer (e.g. fx_fl_t3_paladin_slash).
   * Primary swing read; particles are punctuation only.
   */
  spriteFxId?: string;
  /** Where to place the FX sprite (default origin). */
  spriteFxAt?: SpriteFxAt;
  /** Art pack that owns spriteFxId (default: call-site pack). */
  spriteFxPackId?: string;
};

export type AttackFxTables = {
  defaults: AttackFxRoleConfig;
  byRole: Record<string, Partial<AttackFxRoleConfig>>;
};

const DEFAULT_FX: AttackFxRoleConfig = {
  delivery: "arc",
  impactFrac: 0.35,
  speedCellsPerSec: 14,
  travelMinSec: 0.08,
  travelMaxSec: 0.35,
  originPreset: "df.origin.swing",
  flightPreset: "df.volume.slash",
  hitPreset: "df.hit.slash",
  takenHitPreset: "df.hit.taken",
  volumeRadius: 0.55,
};

export function resolveAttackFx(
  tables: AttackFxTables | undefined,
  role: string,
): AttackFxRoleConfig {
  const d = tables?.defaults ?? DEFAULT_FX;
  const o = tables?.byRole?.[role] ?? {};
  return {
    delivery: (o.delivery ?? d.delivery) as AttackDelivery,
    impactFrac: Number(o.impactFrac ?? d.impactFrac) || 0.35,
    speedCellsPerSec: Number(o.speedCellsPerSec ?? d.speedCellsPerSec) || 14,
    travelMinSec: Number(o.travelMinSec ?? d.travelMinSec) || 0.08,
    travelMaxSec: Number(o.travelMaxSec ?? d.travelMaxSec) || 0.35,
    originPreset: String(o.originPreset ?? d.originPreset),
    flightPreset: String(o.flightPreset ?? d.flightPreset),
    hitPreset: String(o.hitPreset ?? d.hitPreset),
    takenHitPreset: String(
      o.takenHitPreset ?? d.takenHitPreset ?? "df.hit.taken",
    ),
    volumeRadius: Number(o.volumeRadius ?? d.volumeRadius ?? 0.55),
    spriteFxId: o.spriteFxId ?? d.spriteFxId,
    spriteFxAt: (o.spriteFxAt ?? d.spriteFxAt ?? "origin") as SpriteFxAt,
    spriteFxPackId: o.spriteFxPackId ?? d.spriteFxPackId,
  };
}

/** Hit preset: ally victims use taken reaction; others use attacker-role impact. */
export function resolveHitPreset(
  fx: AttackFxRoleConfig,
  targetKind?: "ally" | "enemy" | "structure" | "barrier",
): string {
  if (targetKind === "ally") {
    return fx.takenHitPreset ?? "df.hit.taken";
  }
  return fx.hitPreset;
}

/** Travel time (sec) from delivery + distance in cell units. */
export function attackTravelSec(
  fx: AttackFxRoleConfig,
  distCells: number,
  swingSec: number,
): number {
  const swing = Math.max(0.08, swingSec);
  if (fx.delivery === "bolt") {
    const speed = Math.max(1, fx.speedCellsPerSec ?? 14);
    const t = distCells / speed;
    const lo = fx.travelMinSec ?? 0.08;
    const hi = fx.travelMaxSec ?? 0.35;
    return Math.max(lo, Math.min(hi, t));
  }
  const frac = Math.max(0.1, Math.min(0.9, fx.impactFrac));
  return Math.max(0.04, swing * frac);
}

export function attackProgress(remain: number, duration: number): number {
  const dur = Math.max(0.001, duration);
  return Math.max(0, Math.min(1, 1 - remain / dur));
}

export function lerp2(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  t: number,
): { x: number; y: number } {
  const u = Math.max(0, Math.min(1, t));
  return { x: x0 + (x1 - x0) * u, y: y0 + (y1 - y0) * u };
}

/**
 * Pixel-space visual travel (ms). Profiles:
 * - bolt: FFA default 0.35 / 80–220 (override mul/min/max for Frontline bolt)
 * - artillery: FFA 0.55 / 140–420
 * - siege: Frontline shell 0.7 / 180–520
 * - catalog: attackTravelSec × 1000 (dist in cells via cellPx)
 */
export type VisualTravelOpts = {
  kind: "bolt" | "artillery" | "siege" | "catalog";
  fx?: AttackFxRoleConfig;
  swingSec?: number;
  /** Pixels per cell when kind === "catalog". Default 1 (coords already in cells). */
  cellPx?: number;
  minMs?: number;
  maxMs?: number;
  mul?: number;
};

const TRAVEL_PROFILES: Record<
  "bolt" | "artillery" | "siege",
  { mul: number; minMs: number; maxMs: number }
> = {
  bolt: { mul: 0.35, minMs: 80, maxMs: 220 },
  artillery: { mul: 0.55, minMs: 140, maxMs: 420 },
  siege: { mul: 0.7, minMs: 180, maxMs: 520 },
};

export function visualTravelMs(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
  opts: VisualTravelOpts,
): number {
  if (opts.kind === "catalog") {
    const fx = opts.fx;
    if (!fx) return 0;
    const cellPx = Math.max(1e-6, opts.cellPx ?? 1);
    const distCells = Math.hypot(x1 - x0, y1 - y0) / cellPx;
    return attackTravelSec(fx, distCells, opts.swingSec ?? 0.4) * 1000;
  }
  const base = TRAVEL_PROFILES[opts.kind];
  const mul = opts.mul ?? base.mul;
  const minMs = opts.minMs ?? base.minMs;
  const maxMs = opts.maxMs ?? base.maxMs;
  const d = Math.hypot(x1 - x0, y1 - y0);
  return Math.max(minMs, Math.min(maxMs, d * mul));
}

/** Screen-space impact angle from Hit attack corridor (attacker → victim). */
export function hitImpactAngle(ev: {
  x0?: number;
  y0?: number;
  x1?: number;
  y1?: number;
}): number | undefined {
  if (
    ev.x0 == null ||
    ev.y0 == null ||
    ev.x1 == null ||
    ev.y1 == null
  ) {
    return undefined;
  }
  return Math.atan2(ev.y1 - ev.y0, ev.x1 - ev.x0);
}

